import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useAuth } from "../AuthContext";
import api, { errMsg } from "../api";
import {
    Loader2, RefreshCw, Send, AlertTriangle, Users, ChevronLeft, Wifi, WifiOff, QrCode,
    Paperclip, FileText, Smile, Forward, Star, Trash2, Reply, X, Mic, MoreVertical, Search, Settings,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

/**
 * MyTACS Business WhatsApp — personal WhatsApp Web bridge.
 * Features: messaging, media (img/video/audio/docs), quoted replies, reactions,
 * forward/star/delete, voice notes, group info, search.
 *
 * NOTE: Uses unofficial whatsapp-web.js — violates WhatsApp ToS. Use at own risk.
 */
export default function WhatsAppPage() {
    const { lang } = useAuth();
    const [status, setStatus] = useState(null);
    const [convs, setConvs] = useState([]);
    const [active, setActive] = useState(null);
    const [messages, setMessages] = useState([]);
    const [reply, setReply] = useState("");
    const [sending, setSending] = useState(false);
    const [wsLive, setWsLive] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [replyingTo, setReplyingTo] = useState(null);          // message to quote
    const [emojiPickerFor, setEmojiPickerFor] = useState(null);  // msg id
    const [forwardingMsg, setForwardingMsg] = useState(null);    // msg to forward
    const [showGroupInfo, setShowGroupInfo] = useState(false);
    const [search, setSearch] = useState("");
    const [chatSearch, setChatSearch] = useState("");
    const [recording, setRecording] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const wsRef = useRef(null);
    const scrollRef = useRef(null);
    const fileInputRef = useRef(null);
    const recorderRef = useRef(null);
    const recorderChunksRef = useRef([]);

    const loadStatus = useCallback(async () => {
        try {
            const { data } = await api.get("/wa/status");
            setStatus(data);
        } catch (e) {
            setStatus({ bridge_reachable: false });
        }
    }, []);

    const loadConvs = useCallback(async () => {
        try {
            const { data } = await api.get("/wa/conversations?limit=300");
            setConvs(data || []);
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        }
    }, []);

    const loadMessages = useCallback(async (chat) => {
        if (!chat?.id) return;
        setActive(chat);
        setReplyingTo(null);
        setEmojiPickerFor(null);
        setSearch("");
        try {
            const { data } = await api.get(`/wa/messages?chat_id=${encodeURIComponent(chat.id)}&limit=500`);
            setMessages(data || []);
            setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 60);
            // Mark as read
            api.post("/wa/mark-read", { chat_id: chat.id }).catch(() => {});
        } catch (e) { toast.error(errMsg(e, "Error")); }
    }, []);

    const statusReadyRef = useRef(false);
    useEffect(() => { statusReadyRef.current = !!status?.ready; }, [status?.ready]);

    useEffect(() => {
        const loadAll = () => {
            loadStatus();
            if (statusReadyRef.current) loadConvs();
        };
        loadAll();
        const id = setInterval(loadAll, 4000);
        return () => clearInterval(id);
    }, [loadStatus, loadConvs]);

    // WebSocket for live updates
    useEffect(() => {
        if (!status?.ready) return;
        const token = localStorage.getItem("taskflow_token");
        const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
        const url = `${proto}//${window.location.host}/api/ws/wa?token=${encodeURIComponent(token)}`;
        let ws;
        try {
            ws = new WebSocket(url);
            wsRef.current = ws;
            ws.onopen = () => setWsLive(true);
            ws.onclose = () => setWsLive(false);
            ws.onmessage = (ev) => {
                try {
                    const data = JSON.parse(ev.data);
                    if (data.type === "chat") {
                        setConvs((prev) => {
                            const others = prev.filter((c) => c.id !== data.chat.id);
                            return [{ ...data.chat }, ...others].sort((a, b) => (b.last_message_at || "").localeCompare(a.last_message_at || ""));
                        });
                    } else if (data.type === "message") {
                        const msg = data.message;
                        setConvs((prev) => {
                            const others = prev.filter((c) => c.id !== msg.chat_id);
                            const existing = prev.find((c) => c.id === msg.chat_id);
                            const updated = existing
                                ? { ...existing, last_message_at: msg.created_at, last_message_text: (msg.body || (msg.has_media ? "📎 Medien" : "")).slice(0, 120), last_direction: msg.direction }
                                : { id: msg.chat_id, name: data.chat?.name, is_group: data.chat?.is_group, last_message_at: msg.created_at, last_message_text: (msg.body || "").slice(0, 120), last_direction: msg.direction };
                            return [updated, ...others];
                        });
                        setActive((cur) => {
                            if (cur?.id === msg.chat_id) {
                                setMessages((prev) => prev.find((p) => p.id === msg.id) ? prev : [...prev, msg]);
                                setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
                            }
                            return cur;
                        });
                        if (msg.direction === "inbound" && !msg.from_me) {
                            toast(data.chat?.name || msg.sender_name || "WhatsApp", {
                                description: (msg.body || "📎 Medien").slice(0, 100),
                                duration: 4000,
                            });
                        }
                    } else if (data.type === "ack") {
                        setMessages((prev) => prev.map((m) => m.id === data.id ? { ...m, ack: data.ack } : m));
                    } else if (data.type === "reaction") {
                        setMessages((prev) => prev.map((m) => m.id === data.id ? { ...m, reactions: data.reactions } : m));
                    } else if (data.type === "revoke") {
                        setMessages((prev) => prev.map((m) => m.id === data.id ? { ...m, is_deleted: true, body: "" } : m));
                    }
                } catch { /* ignore parse errors */ }
            };
        } catch { /* websocket can fail */ }
        return () => { try { ws?.close(); } catch { /* */ } };
    }, [status?.ready]);

    // ---- Actions ----
    const sendMessage = async () => {
        if (!reply.trim() || !active) return;
        setSending(true);
        try {
            const body = { chat_id: active.id, text: reply };
            if (replyingTo) body.quoted_msg_id = replyingTo.id;
            await api.post("/wa/send", body);
            setReply("");
            setReplyingTo(null);
        } catch (e) {
            toast.error(errMsg(e, "Senden fehlgeschlagen"));
        } finally { setSending(false); }
    };

    const onPickFile = () => fileInputRef.current?.click();
    const onFileChosen = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f || !active) return;
        if (f.size > 100 * 1024 * 1024) {
            toast.error(lang === "de" ? "Datei zu groß (max. 100 MB)" : "File too large (max 100 MB)");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("chat_id", active.id);
            fd.append("caption", reply || "");
            fd.append("file", f);
            if (replyingTo) fd.append("quoted_msg_id", replyingTo.id);
            await api.post("/wa/send-media", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setReply("");
            setReplyingTo(null);
            toast.success(lang === "de" ? "Anhang gesendet" : "Attachment sent");
        } catch (err) {
            toast.error(errMsg(err, lang === "de" ? "Anhang fehlgeschlagen" : "Attachment failed"));
        } finally { setUploading(false); }
    };

    const react = async (msgId, emoji) => {
        setEmojiPickerFor(null);
        try { await api.post("/wa/react", { msg_id: msgId, emoji }); }
        catch (e) { toast.error(errMsg(e, "Reaktion fehlgeschlagen")); }
    };

    const toggleStar = async (msg) => {
        try {
            await api.post("/wa/star", { msg_id: msg.id, starred: !msg.is_starred });
            setMessages((prev) => prev.map((m) => m.id === msg.id ? { ...m, is_starred: !msg.is_starred } : m));
        } catch (e) { toast.error(errMsg(e, "Fehler")); }
    };

    const deleteMsg = async (msg) => {
        const forEveryone = msg.from_me && window.confirm(lang === "de"
            ? "Für alle löschen? (OK = für alle, Abbrechen = nur für mich)"
            : "Delete for everyone? (OK = everyone, Cancel = only me)");
        try {
            await api.post("/wa/delete-msg", { msg_id: msg.id, for_everyone: forEveryone });
        } catch (e) { toast.error(errMsg(e, "Löschen fehlgeschlagen")); }
    };

    const forwardMsg = async (chatId) => {
        if (!forwardingMsg) return;
        try {
            await api.post("/wa/forward", { msg_id: forwardingMsg.id, chat_id: chatId });
            toast.success(lang === "de" ? "Weitergeleitet" : "Forwarded");
            setForwardingMsg(null);
        } catch (e) { toast.error(errMsg(e, "Weiterleiten fehlgeschlagen")); }
    };

    const pair = async () => {
        if (!window.confirm(lang === "de" ? "Bridge erneut koppeln? Bestehende Session wird beendet." : "Re-pair the bridge?")) return;
        try {
            await api.post("/wa/pair");
            toast.success(lang === "de" ? "Neuer QR-Code wird generiert..." : "Generating new QR...");
            loadStatus();
        } catch (e) { toast.error(errMsg(e, "Error")); }
    };

    // Voice note recording
    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
            recorderChunksRef.current = [];
            rec.ondataavailable = (e) => { if (e.data.size > 0) recorderChunksRef.current.push(e.data); };
            rec.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                const blob = new Blob(recorderChunksRef.current, { type: "audio/webm" });
                if (blob.size === 0 || !active) return;
                setUploading(true);
                try {
                    const fd = new FormData();
                    fd.append("chat_id", active.id);
                    fd.append("caption", "");
                    fd.append("file", blob, "voice.webm");
                    fd.append("send_as_voice", "true");
                    if (replyingTo) fd.append("quoted_msg_id", replyingTo.id);
                    await api.post("/wa/send-media", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    setReplyingTo(null);
                    toast.success(lang === "de" ? "Sprachnachricht gesendet" : "Voice note sent");
                } catch (err) {
                    toast.error(errMsg(err, "Senden fehlgeschlagen"));
                } finally { setUploading(false); }
            };
            recorderRef.current = rec;
            rec.start();
            setRecording(true);
        } catch (e) {
            toast.error(lang === "de" ? "Mikrofonzugriff abgelehnt" : "Microphone denied");
        }
    };
    const stopRecording = () => {
        try { recorderRef.current?.stop(); } catch { /* already stopped */ }
        setRecording(false);
    };

    // Filtered + sorted conversations
    const filteredConvs = useMemo(() => {
        const q = chatSearch.trim().toLowerCase();
        const arr = q ? convs.filter((c) => (c.name || "").toLowerCase().includes(q)) : convs;
        // Sort by last_message_at DESC (newest chat first)
        return [...arr].sort((a, b) => (b.last_message_at || "").localeCompare(a.last_message_at || ""));
    }, [convs, chatSearch]);
    // Filtered + sorted messages — defensive ASC sort so live + history messages always render chronologically
    const filteredMsgs = useMemo(() => {
        const q = search.trim().toLowerCase();
        const arr = q
            ? messages.filter((m) => (m.body || "").toLowerCase().includes(q) || (m.sender_name || "").toLowerCase().includes(q))
            : messages;
        return [...arr].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    }, [messages, search]);

    // ---- Renders ----
    if (status?.bridge_reachable === false) {
        return (
            <div className="p-6 max-w-2xl mx-auto">
                <div className="rounded-lg border border-red-300 bg-red-50 p-6 mt-4 text-center">
                    <AlertTriangle className="w-8 h-8 text-red-700 mx-auto mb-3" />
                    <p className="font-semibold">{lang === "de" ? "Bridge-Service nicht erreichbar" : "Bridge service unreachable"}</p>
                    <p className="text-sm text-red-700 mt-1">{lang === "de" ? "Der Business-WhatsApp-Dienst ist offline. Bitte Admin kontaktieren." : "The Business WhatsApp service is offline."}</p>
                </div>
            </div>
        );
    }
    if (status && !status.ready) {
        return (
            <div className="p-6 max-w-2xl mx-auto">
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-6 mt-4 text-center">
                    <QrCode className="w-8 h-8 text-amber-700 mx-auto mb-2" />
                    <h2 className="text-lg font-bold">{lang === "de" ? "Business WhatsApp koppeln" : "Pair Business WhatsApp"}</h2>
                    <p className="text-sm text-neutral-700 mt-1 mb-4">
                        {lang === "de"
                            ? "Öffne WhatsApp auf deinem Handy → Einstellungen → Verknüpfte Geräte → Gerät verknüpfen — und scanne den QR-Code:"
                            : "Open WhatsApp on your phone → Settings → Linked devices → Link a device — and scan the QR code:"}
                    </p>
                    {status.qr ? (
                        <div className="bg-white inline-block p-3 rounded-lg border border-amber-300">
                            <QRRenderer text={status.qr} />
                        </div>
                    ) : (
                        <p className="text-xs text-neutral-500 mt-2"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> {lang === "de" ? "QR wird generiert..." : "Generating QR..."}</p>
                    )}
                    <p className="text-[11px] text-neutral-500 mt-4">{lang === "de" ? "Der QR-Code ist nur 20 Sek. gültig — falls abgelaufen, lädt die Seite automatisch einen neuen." : "QR is valid for ~20s — page auto-refreshes."}</p>
                    <Button variant="outline" size="sm" onClick={pair} className="mt-3" data-testid="wa-repair-btn">
                        <RefreshCw className="w-3.5 h-3.5 mr-1" /> {lang === "de" ? "Neuer QR" : "New QR"}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-[calc(100vh-3rem)] bg-neutral-50">
            {/* Sidebar — hidden on mobile when a chat is active */}
            <div className={`${active ? "hidden md:flex" : "flex"} w-full md:w-80 border-r border-neutral-200 bg-white flex-col`}>
                <div className="px-3 py-2 border-b border-neutral-200 flex items-center justify-between">
                    <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">Business WhatsApp</p>
                        <p className="text-xs text-neutral-700 font-medium">{status?.name || "—"}</p>
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setShowSettings(true)}
                            className="text-neutral-500 hover:text-emerald-600 p-1 rounded"
                            title={lang === "de" ? "Auto-Task Einstellungen" : "Auto-Task Settings"}
                            data-testid="wa-settings-btn"
                        >
                            <Settings className="w-4 h-4" />
                        </button>
                        <span className={`text-[10px] font-semibold flex items-center gap-1 px-2 py-0.5 rounded-full ${wsLive ? "bg-emerald-100 text-emerald-800" : "bg-neutral-100 text-neutral-500"}`} data-testid="wa-live">
                            {wsLive ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                            {wsLive ? "Live" : "Off"}
                        </span>
                    </div>
                </div>
                <div className="px-3 py-2 border-b border-neutral-100 flex items-center gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
                        <input
                            value={chatSearch}
                            onChange={(e) => setChatSearch(e.target.value)}
                            placeholder={lang === "de" ? "Chat suchen..." : "Search chat..."}
                            className="pl-7 pr-2 py-1 text-xs border border-neutral-200 rounded w-full focus:outline-none focus:ring-1 focus:ring-emerald-500"
                            data-testid="wa-conv-search"
                        />
                    </div>
                    <Button size="sm" variant="ghost" onClick={loadConvs} className="h-7 px-2" data-testid="wa-refresh"><RefreshCw className="w-3.5 h-3.5" /></Button>
                </div>
                <div className="px-3 py-1 text-[10px] text-neutral-500">{filteredConvs.length} {lang === "de" ? "Chats" : "Chats"}</div>
                <div className="flex-1 overflow-y-auto" data-testid="wa-conv-list">
                    {filteredConvs.length === 0 ? (
                        <p className="text-center text-xs text-neutral-500 p-6">{lang === "de" ? "Keine Chats" : "No chats"}</p>
                    ) : filteredConvs.map((c) => (
                        <ConvRow key={c.id} c={c} active={active?.id === c.id} onClick={() => loadMessages(c)} />
                    ))}
                </div>
            </div>

            {/* Chat area — hidden on mobile when no active */}
            <div className={`${active ? "flex" : "hidden md:flex"} flex-1 flex-col min-w-0`}>
                {!active ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-neutral-400">
                        <div className="text-5xl mb-3">💬</div>
                        <p className="text-sm">{lang === "de" ? "Wähle links einen Chat aus" : "Pick a chat on the left"}</p>
                    </div>
                ) : (
                    <>
                        <header className="bg-white border-b border-neutral-200 px-4 py-2 flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => setActive(null)} className="md:hidden -ml-1.5 px-1.5" data-testid="wa-back-btn"><ChevronLeft className="w-5 h-5" /></Button>
                            <button
                                disabled={!active.is_group}
                                onClick={() => active.is_group && setShowGroupInfo(true)}
                                className="flex items-center gap-2 flex-1 min-w-0 text-left hover:bg-neutral-50 rounded px-1 py-0.5"
                                data-testid="wa-chat-header"
                            >
                                <Avatar isGroup={active.is_group} name={active.name} />
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-sm truncate">{active.name}</p>
                                    <p className="text-[11px] text-neutral-500">
                                        {active.is_group
                                            ? (lang === "de" ? "Gruppe · " : "Group · ") + (active.participants?.length || 0) + " " + (lang === "de" ? "Mitglieder · tippen für Info" : "members · tap for info")
                                            : "Privat"}
                                    </p>
                                </div>
                            </button>
                            <div className="relative hidden sm:block">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
                                <input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder={lang === "de" ? "In Chat suchen..." : "Search..."}
                                    className="pl-7 pr-2 py-1 text-xs border border-neutral-200 rounded w-44 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                    data-testid="wa-msg-search"
                                />
                            </div>
                        </header>

                        {/* Messages */}
                        <div ref={scrollRef} className="flex-1 overflow-y-auto bg-neutral-100 p-2 sm:p-4" data-testid="wa-messages">
                            <MessageList
                                messages={filteredMsgs}
                                active={active}
                                onReact={react}
                                onReply={setReplyingTo}
                                onForward={setForwardingMsg}
                                onStar={toggleStar}
                                onDelete={deleteMsg}
                                emojiPickerFor={emojiPickerFor}
                                setEmojiPickerFor={setEmojiPickerFor}
                                setMessages={setMessages}
                                lang={lang}
                            />
                        </div>

                        {/* Reply preview */}
                        {replyingTo && (
                            <div className="bg-emerald-50 border-t border-emerald-200 px-4 py-2 flex items-start gap-2" data-testid="wa-reply-preview">
                                <Reply className="w-4 h-4 text-emerald-700 mt-0.5" />
                                <div className="flex-1 min-w-0 border-l-2 border-emerald-500 pl-2">
                                    <p className="text-[10px] font-semibold text-emerald-800">{replyingTo.sender_name || (replyingTo.from_me ? "Du" : "")}</p>
                                    <p className="text-xs text-neutral-700 truncate">{replyingTo.body || (replyingTo.has_media ? "📎 Medien" : "")}</p>
                                </div>
                                <button onClick={() => setReplyingTo(null)} className="text-neutral-500 hover:text-red-600"><X className="w-4 h-4" /></button>
                            </div>
                        )}

                        {/* Footer */}
                        <footer className="bg-white border-t border-neutral-200 p-3 flex items-center gap-2">
                            <input
                                ref={fileInputRef}
                                type="file"
                                onChange={onFileChosen}
                                className="hidden"
                                data-testid="wa-file-input"
                                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.txt"
                            />
                            <Button
                                onClick={onPickFile}
                                disabled={uploading || sending || recording}
                                variant="ghost"
                                size="icon"
                                title={lang === "de" ? "Anhang hochladen" : "Attach file"}
                                data-testid="wa-attach-btn"
                            >
                                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                            </Button>
                            <Input
                                value={reply}
                                onChange={(e) => setReply(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                                placeholder={lang === "de" ? "Antwort schreiben..." : "Write a reply..."}
                                disabled={sending || recording}
                                data-testid="wa-reply-input"
                            />
                            {reply.trim() ? (
                                <Button onClick={sendMessage} disabled={sending || !reply.trim()} className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="wa-send-btn">
                                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                </Button>
                            ) : recording ? (
                                <Button onClick={stopRecording} className="bg-red-600 hover:bg-red-700 text-white animate-pulse" data-testid="wa-stop-record-btn">
                                    <Mic className="w-4 h-4" /> Stop
                                </Button>
                            ) : (
                                <Button onClick={startRecording} disabled={uploading} variant="outline" title={lang === "de" ? "Sprachnachricht aufnehmen" : "Record voice"} data-testid="wa-record-btn">
                                    <Mic className="w-4 h-4" />
                                </Button>
                            )}
                        </footer>
                    </>
                )}
            </div>

            {/* Group info dialog */}
            {showGroupInfo && active?.is_group && (
                <GroupInfoDialog chatId={active.id} chatName={active.name} onClose={() => setShowGroupInfo(false)} />
            )}
            {/* Forward picker */}
            {forwardingMsg && (
                <ForwardPicker convs={convs} onClose={() => setForwardingMsg(null)} onPick={forwardMsg} />
            )}
            {/* Auto-Task settings */}
            {showSettings && (
                <AutoTaskSettingsDialog onClose={() => setShowSettings(false)} lang={lang} />
            )}
        </div>
    );
}

// ============= Sub-components =============

function AutoTaskSettingsDialog({ onClose, lang }) {
    const [settings, setSettings] = useState({ enabled: false, space_id: null, priority: "medium" });
    const [spaces, setSpaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [s, sp] = await Promise.all([
                    api.get("/wa/auto-task-settings"),
                    api.get("/spaces"),
                ]);
                setSettings(s.data || { enabled: false });
                setSpaces(sp.data || []);
            } catch (e) {
                toast.error(errMsg(e, "Konnte Einstellungen nicht laden"));
            } finally { setLoading(false); }
        })();
    }, []);

    const save = async () => {
        if (settings.enabled && !settings.space_id) {
            toast.error(lang === "de" ? "Bitte Abteilung wählen" : "Please select a space");
            return;
        }
        setSaving(true);
        try {
            await api.put("/wa/auto-task-settings", settings);
            toast.success(lang === "de" ? "Gespeichert" : "Saved");
            onClose();
        } catch (e) { toast.error(errMsg(e, "Speichern fehlgeschlagen")); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="wa-settings-dialog">
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                <header className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
                    <div>
                        <p className="font-semibold text-sm">{lang === "de" ? "Auto-Tasks aus WhatsApp" : "Auto-Tasks from WhatsApp"}</p>
                        <p className="text-[11px] text-neutral-500">{lang === "de" ? "Erstelle automatisch eine Aufgabe bei jeder eingehenden WhatsApp-Nachricht" : "Auto-create a task for every inbound WhatsApp message"}</p>
                    </div>
                    <button onClick={onClose} className="text-neutral-500"><X className="w-5 h-5" /></button>
                </header>
                {loading ? (
                    <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>
                ) : (
                    <div className="p-4 space-y-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={settings.enabled}
                                onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
                                className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500"
                                data-testid="wa-settings-enabled"
                            />
                            <span className="text-sm font-medium">{lang === "de" ? "Auto-Tasks aktivieren" : "Enable auto-tasks"}</span>
                        </label>
                        <div>
                            <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{lang === "de" ? "Abteilung (Space)" : "Department (Space)"}</label>
                            <select
                                value={settings.space_id || ""}
                                onChange={(e) => setSettings({ ...settings, space_id: e.target.value || null })}
                                disabled={!settings.enabled}
                                className="w-full border border-neutral-300 rounded px-3 py-2 text-sm focus:ring-1 focus:ring-emerald-500 disabled:bg-neutral-100"
                                data-testid="wa-settings-space"
                            >
                                <option value="">— {lang === "de" ? "Wählen" : "Pick"} —</option>
                                {spaces.map((s) => (
                                    <option key={s.id} value={s.id}>{s.name}</option>
                                ))}
                            </select>
                            <p className="text-[10px] text-neutral-500 mt-1">{lang === "de" ? "Tasks werden allen Mitgliedern dieser Abteilung sichtbar." : "Tasks will be visible to all members of this space."}</p>
                        </div>
                        <div>
                            <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{lang === "de" ? "Priorität" : "Priority"}</label>
                            <select
                                value={settings.priority || "medium"}
                                onChange={(e) => setSettings({ ...settings, priority: e.target.value })}
                                disabled={!settings.enabled}
                                className="w-full border border-neutral-300 rounded px-3 py-2 text-sm focus:ring-1 focus:ring-emerald-500 disabled:bg-neutral-100"
                                data-testid="wa-settings-priority"
                            >
                                <option value="low">{lang === "de" ? "Niedrig" : "Low"}</option>
                                <option value="medium">{lang === "de" ? "Mittel" : "Medium"}</option>
                                <option value="high">{lang === "de" ? "Hoch" : "High"}</option>
                            </select>
                        </div>
                        <div className="text-[11px] text-neutral-500 bg-amber-50 border border-amber-200 rounded p-2">
                            <p>{lang === "de" ? "Hinweis: Pro Chat wird nur EINE offene Aufgabe erstellt. Bei deiner Antwort wechselt sie automatisch auf In Bearbeitung." : "Note: One open task per chat. Replying flips it to 'In progress'."}</p>
                        </div>
                    </div>
                )}
                <footer className="px-4 py-3 border-t border-neutral-200 flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose} data-testid="wa-settings-cancel">{lang === "de" ? "Abbrechen" : "Cancel"}</Button>
                    <Button onClick={save} disabled={saving} className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="wa-settings-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (lang === "de" ? "Speichern" : "Save")}
                    </Button>
                </footer>
            </div>
        </div>
    );
}

function Avatar({ isGroup, name }) {
    const initial = (name || "?")[0]?.toUpperCase() || "?";
    return (
        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-base font-semibold shrink-0 ${isGroup ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>
            {isGroup ? <Users className="w-4 h-4" /> : initial}
        </div>
    );
}

function ConvRow({ c, active, onClick }) {
    return (
        <button onClick={onClick} data-testid={`wa-conv-${c.id}`}
            className={`w-full flex items-start gap-2 p-3 border-b border-neutral-100 text-left transition ${active ? "bg-emerald-50" : "hover:bg-neutral-50"}`}>
            <Avatar isGroup={c.is_group} name={c.name} />
            <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-1">
                    <p className="text-sm font-semibold truncate flex-1">{c.name || c.id}</p>
                    {c.last_message_at && <span className="text-[10px] text-neutral-400 whitespace-nowrap">{formatTimeShort(c.last_message_at)}</span>}
                </div>
                <p className="text-[11px] text-neutral-500 truncate">{c.last_message_text || ""}</p>
            </div>
            {c.unread_count > 0 && (
                <span className="bg-emerald-500 text-white text-[10px] font-semibold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">{c.unread_count}</span>
            )}
        </button>
    );
}

function MessageList({ messages, active, onReact, onReply, onForward, onStar, onDelete, emojiPickerFor, setEmojiPickerFor, setMessages, lang }) {
    if (messages.length === 0) {
        return <p className="text-center text-xs text-neutral-500 mt-6">{lang === "de" ? "Noch keine Nachrichten" : "No messages yet"}</p>;
    }
    // Insert date dividers
    const groups = [];
    let lastDay = null;
    for (const m of messages) {
        const day = new Date(m.created_at).toDateString();
        if (day !== lastDay) {
            groups.push({ type: "divider", day, key: `div-${day}-${m.id}` });
            lastDay = day;
        }
        groups.push({ type: "msg", msg: m, key: m.id });
    }
    return (
        <div className="space-y-1">
            {groups.map((g) => g.type === "divider" ? (
                <div key={g.key} className="text-center my-3" data-testid={`wa-date-${g.day}`}>
                    <span className="bg-white text-neutral-600 text-[11px] font-semibold rounded-full px-3 py-1 shadow-sm">{formatDateLabel(g.day, lang)}</span>
                </div>
            ) : (
                <MessageBubble
                    key={g.key}
                    m={g.msg}
                    active={active}
                    onReact={onReact}
                    onReply={onReply}
                    onForward={onForward}
                    onStar={onStar}
                    onDelete={onDelete}
                    emojiPickerFor={emojiPickerFor}
                    setEmojiPickerFor={setEmojiPickerFor}
                    setMessages={setMessages}
                />
            ))}
        </div>
    );
}

function MessageBubble({ m, active, onReact, onReply, onForward, onStar, onDelete, emojiPickerFor, setEmojiPickerFor, setMessages }) {
    const [menuOpen, setMenuOpen] = useState(false);
    if (m.is_deleted) {
        return (
            <div className={`flex ${m.from_me ? "justify-end" : "justify-start"}`} data-testid={`wa-msg-${m.id}`}>
                <div className="max-w-[70%] rounded-lg p-2 bg-neutral-200 text-neutral-500 italic text-xs">
                    🚫 Diese Nachricht wurde gelöscht
                </div>
            </div>
        );
    }
    return (
        <div className={`flex ${m.from_me ? "justify-end" : "justify-start"} group`} data-testid={`wa-msg-${m.id}`}>
            <div className={`relative max-w-[85%] sm:max-w-[70%] rounded-lg p-2 shadow-sm ${m.from_me ? "bg-emerald-100 text-emerald-900 rounded-tr-none" : "bg-white rounded-tl-none"}`}>
                {/* Group sender */}
                {active.is_group && !m.from_me && m.sender_name && (
                    <p className="text-[11px] font-bold text-blue-700 mb-0.5">{m.sender_name}</p>
                )}
                {/* Forward badge */}
                {m.is_forwarded && (
                    <p className="text-[10px] text-neutral-500 italic mb-1 flex items-center gap-1"><Forward className="w-3 h-3" /> Weitergeleitet</p>
                )}
                {/* Quoted reply */}
                {m.quoted && (
                    <div className="bg-black/5 border-l-2 border-emerald-500 px-2 py-1 mb-1 rounded text-[11px]">
                        <p className="font-semibold text-emerald-700">{m.quoted.sender_name || "Antwort"}</p>
                        <p className="text-neutral-700 truncate">{m.quoted.body || (m.quoted.has_media ? "📎 Medien" : "")}</p>
                    </div>
                )}
                {/* Media */}
                {m.has_media && m.media?.filename ? (
                    <MediaPreview media={m.media} />
                ) : m.has_media ? (
                    <MediaLoader msgId={m.id} onLoaded={(media) => {
                        setMessages?.((prev) => prev.map((mm) => mm.id === m.id ? { ...mm, media } : mm));
                    }} />
                ) : null}
                {/* Body */}
                {m.body && (
                    <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
                )}
                {/* Footer: time + star + ack */}
                <p className="text-[9px] text-neutral-500 text-right mt-0.5 flex items-center justify-end gap-1">
                    {m.is_starred && <Star className="w-2.5 h-2.5 fill-amber-400 text-amber-500" />}
                    {new Date(m.created_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
                    {m.from_me && (
                        <span title={`ack ${m.ack}`} className={m.ack >= 3 ? "text-blue-500" : ""}>
                            {m.ack >= 2 ? "✓✓" : m.ack >= 1 ? "✓" : "·"}
                        </span>
                    )}
                </p>
                {/* Reactions */}
                {(m.reactions || []).length > 0 && (
                    <div className="absolute -bottom-2 left-1 bg-white rounded-full shadow border border-neutral-200 px-1.5 py-0.5 text-xs flex items-center gap-0.5">
                        {(m.reactions || []).map((r, i) => (
                            <span key={i} title={`${r.count} Reaktion${r.count > 1 ? "en" : ""}`}>{r.emoji}{r.count > 1 ? r.count : ""}</span>
                        ))}
                    </div>
                )}

                {/* Hover actions */}
                <div className={`absolute -top-3 ${m.from_me ? "right-1" : "left-1"} hidden group-hover:flex bg-white shadow-md rounded-full border border-neutral-200 px-1 py-0.5 gap-0.5 z-10`}>
                    <button onClick={(e) => { e.stopPropagation(); setEmojiPickerFor(emojiPickerFor === m.id ? null : m.id); }}
                        title="Reagieren" className="p-1 hover:bg-neutral-100 rounded-full" data-testid={`wa-react-btn-${m.id}`}>
                        <Smile className="w-3.5 h-3.5 text-neutral-600" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); onReply(m); }}
                        title="Antworten" className="p-1 hover:bg-neutral-100 rounded-full" data-testid={`wa-reply-btn-${m.id}`}>
                        <Reply className="w-3.5 h-3.5 text-neutral-600" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); setMenuOpen((o) => !o); }}
                        title="Mehr" className="p-1 hover:bg-neutral-100 rounded-full" data-testid={`wa-more-btn-${m.id}`}>
                        <MoreVertical className="w-3.5 h-3.5 text-neutral-600" />
                    </button>
                </div>

                {/* Emoji picker */}
                {emojiPickerFor === m.id && (
                    <div className={`absolute -top-10 ${m.from_me ? "right-0" : "left-0"} bg-white shadow-lg rounded-full border border-neutral-200 px-2 py-1 flex gap-1 z-20`}>
                        {QUICK_REACTIONS.map((e) => (
                            <button key={e} onClick={() => onReact(m.id, e)} className="text-xl hover:scale-125 transition" data-testid={`wa-reaction-${e}`}>
                                {e}
                            </button>
                        ))}
                    </div>
                )}

                {/* Dropdown menu */}
                {menuOpen && (
                    <div className={`absolute top-6 ${m.from_me ? "right-0" : "left-0"} bg-white shadow-lg rounded border border-neutral-200 z-20 text-xs min-w-[140px]`}>
                        <button onClick={() => { setMenuOpen(false); onForward(m); }} className="flex items-center gap-2 px-3 py-2 hover:bg-neutral-50 w-full text-left" data-testid={`wa-forward-${m.id}`}>
                            <Forward className="w-3.5 h-3.5" /> Weiterleiten
                        </button>
                        <button onClick={() => { setMenuOpen(false); onStar(m); }} className="flex items-center gap-2 px-3 py-2 hover:bg-neutral-50 w-full text-left" data-testid={`wa-star-${m.id}`}>
                            <Star className={`w-3.5 h-3.5 ${m.is_starred ? "fill-amber-400 text-amber-500" : ""}`} />
                            {m.is_starred ? "Stern entfernen" : "Mit Stern versehen"}
                        </button>
                        <button onClick={() => { setMenuOpen(false); navigator.clipboard.writeText(m.body || ""); toast.success("Kopiert"); }} className="flex items-center gap-2 px-3 py-2 hover:bg-neutral-50 w-full text-left" data-testid={`wa-copy-${m.id}`}>
                            <FileText className="w-3.5 h-3.5" /> Kopieren
                        </button>
                        <button onClick={() => { setMenuOpen(false); onDelete(m); }} className="flex items-center gap-2 px-3 py-2 hover:bg-red-50 text-red-600 w-full text-left" data-testid={`wa-delete-${m.id}`}>
                            <Trash2 className="w-3.5 h-3.5" /> Löschen
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

function GroupInfoDialog({ chatId, chatName, onClose }) {
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get(`/wa/group/${encodeURIComponent(chatId)}`);
                if (alive) setInfo(data);
            } catch (e) {
                toast.error(errMsg(e, "Gruppen-Info nicht verfügbar"));
            } finally { if (alive) setLoading(false); }
        })();
        return () => { alive = false; };
    }, [chatId]);
    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="wa-group-info">
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                <header className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Avatar isGroup name={chatName} />
                        <div>
                            <p className="font-semibold text-sm">{chatName}</p>
                            <p className="text-[10px] text-neutral-500">Gruppe · {info?.participants_count || 0} Mitglieder</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900"><X className="w-5 h-5" /></button>
                </header>
                {loading ? (
                    <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>
                ) : (
                    <div className="flex-1 overflow-y-auto">
                        {info?.description && (
                            <div className="p-3 border-b border-neutral-100 bg-neutral-50">
                                <p className="text-[10px] text-neutral-500 uppercase font-semibold mb-1">Beschreibung</p>
                                <p className="text-xs text-neutral-700 whitespace-pre-wrap">{info.description}</p>
                            </div>
                        )}
                        <div className="p-2">
                            <p className="text-[10px] text-neutral-500 uppercase font-semibold px-2 py-1">Mitglieder ({info?.participants_count})</p>
                            {(info?.participants || []).map((p) => (
                                <div key={p.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-neutral-50 rounded" data-testid={`wa-member-${p.id}`}>
                                    <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-semibold">{(p.name || "?")[0]}</div>
                                    <span className="text-xs flex-1 truncate">{p.name}</span>
                                    {p.is_super_admin && <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Super-Admin</span>}
                                    {p.is_admin && !p.is_super_admin && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Admin</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function ForwardPicker({ convs, onClose, onPick }) {
    const [q, setQ] = useState("");
    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        return convs.filter((c) => !s || (c.name || "").toLowerCase().includes(s));
    }, [convs, q]);
    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="wa-forward-picker">
            <div className="bg-white rounded-lg shadow-2xl max-w-sm w-full max-h-[70vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                <header className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
                    <p className="font-semibold text-sm">Weiterleiten an...</p>
                    <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900"><X className="w-5 h-5" /></button>
                </header>
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Chat suchen..."
                    className="m-3 px-3 py-1.5 text-xs border border-neutral-200 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    data-testid="wa-forward-search"
                />
                <div className="flex-1 overflow-y-auto">
                    {filtered.map((c) => (
                        <button key={c.id} onClick={() => onPick(c.id)} className="flex items-center gap-2 px-3 py-2 hover:bg-neutral-50 w-full text-left" data-testid={`wa-forward-target-${c.id}`}>
                            <Avatar isGroup={c.is_group} name={c.name} />
                            <span className="text-sm truncate">{c.name}</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function MediaLoader({ msgId, onLoaded }) {
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const load = async () => {
        setLoading(true); setFailed(false);
        try {
            const { data } = await api.post("/wa/fetch-media", { msg_id: msgId });
            onLoaded?.(data.media);
        } catch (e) {
            setFailed(true);
            toast.error(errMsg(e, "Medium konnte nicht geladen werden"));
        } finally { setLoading(false); }
    };
    return (
        <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-2 py-1.5 mb-1 rounded bg-white/60 hover:bg-white border border-dashed border-neutral-300 text-xs text-neutral-600 w-full"
            data-testid={`wa-media-load-${msgId}`}
        >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
            <span className="truncate flex-1 text-left">
                {failed ? "Fehlgeschlagen — nochmal versuchen" : (loading ? "Lade Medium…" : "📎 Medium laden")}
            </span>
        </button>
    );
}

function MediaPreview({ media }) {
    const filename = encodeURIComponent(media.filename);
    const src = `${process.env.REACT_APP_BACKEND_URL}/api/wa/media/${filename}`;
    const mt = (media.mimetype || "").toLowerCase();
    const original = media.original_name || media.filename;

    if (mt.startsWith("image/")) {
        return (
            <a href={src} target="_blank" rel="noreferrer" data-testid="wa-media-image">
                <img src={src} alt={original} className="rounded max-w-full max-h-80 object-contain mb-1" loading="lazy" />
            </a>
        );
    }
    if (mt.startsWith("video/")) {
        return <video src={src} controls className="rounded max-w-full max-h-80 mb-1" preload="metadata" data-testid="wa-media-video" />;
    }
    if (mt.startsWith("audio/")) {
        return <audio src={src} controls className="w-full mb-1" preload="metadata" data-testid="wa-media-audio" />;
    }
    return (
        <a href={src} target="_blank" rel="noreferrer"
           className="flex items-center gap-2 px-2 py-1.5 mb-1 rounded bg-white/60 hover:bg-white border border-neutral-200 text-xs"
           data-testid="wa-media-file">
            <FileText className="w-4 h-4 text-neutral-600 shrink-0" />
            <span className="truncate flex-1">{original}</span>
            {media.size && <span className="text-[10px] text-neutral-500 shrink-0">{(media.size / 1024).toFixed(0)} KB</span>}
        </a>
    );
}

function QRRenderer({ text }) {
    const enc = encodeURIComponent(text);
    return (
        <img
            src={`https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${enc}`}
            alt="WhatsApp QR"
            width={260}
            height={260}
            data-testid="wa-qr-img"
        />
    );
}

// ============= Helpers =============

function formatTimeShort(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
        return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    }
    const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return "Gestern";
    const sameWeek = (now - d) < 7 * 86400 * 1000;
    if (sameWeek) return d.toLocaleDateString("de-DE", { weekday: "short" });
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
}

function formatDateLabel(dayStr, lang) {
    const d = new Date(dayStr);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
    if (d.getTime() === today.getTime()) return lang === "de" ? "Heute" : "Today";
    if (d.getTime() === yesterday.getTime()) return lang === "de" ? "Gestern" : "Yesterday";
    return d.toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
}
